import { create } from 'zustand';
import type { Project } from '@shared/schema';
import { apiRequest } from '@/lib/queryClient';

const toCount = (value: unknown): number => {
  if (typeof value === 'bigint') {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (value && typeof value === 'object') {
    const nested =
      (value as any)?.value ??
      (value as any)?.count ??
      (value as any)?.total ??
      (value as any)?.n ??
      undefined;
    if (nested !== undefined) {
      return toCount(nested);
    }
  }
  return 0;
};

type ProjectWithAggregates = Project & {
  datasetsCount?: number;
  plotsCount?: number;
  publishedPlotsCount?: number;
};

const normalizeProject = (project: any): ProjectWithAggregates => ({
  ...project,
  datasetsCount: toCount(project?.datasetsCount ?? project?.datasets_count),
  plotsCount: toCount(
    project?.plotsCount ??
    project?.publishedPlotsCount ??
    project?.plots_count ??
    project?.published_plots_count,
  ),
  publishedPlotsCount: toCount(
    project?.publishedPlotsCount ??
    project?.plotsCount ??
    project?.published_plots_count ??
    project?.plots_count,
  ),
});

interface ProjectsState {
  projects: ProjectWithAggregates[];
  currentProject: ProjectWithAggregates | null;
  isLoading: boolean;
  error: string | null;
  subscribedProjectId?: string | null;
  subscriptionAckAt?: number | null;
  
  // Actions
  loadProjects: () => Promise<void>;
  setCurrentProject: (project: ProjectWithAggregates | null) => void;
  setSubscriptionAck: (projectId: string | null) => void;
  isSubscribedReady: (projectId?: string | null) => boolean;
  createProject: (name: string, description?: string) => Promise<ProjectWithAggregates>;
  getOrCreateDefaultProject: () => Promise<ProjectWithAggregates>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
}

export const useProjectsStore = create<ProjectsState>((set, get) => ({
  projects: [],
  currentProject: null,
  isLoading: false,
  error: null,
  subscribedProjectId: null,
  subscriptionAckAt: null,
  
  loadProjects: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await apiRequest('GET', `/api/projects`);
      const projects = await response.json();
      const normalized = (Array.isArray(projects) ? projects : []).map(normalizeProject);
      console.debug('[projectsStore] loaded', normalized);
      const currentProject = get().currentProject;
      const refreshedCurrent = currentProject
        ? normalized.find((p: any) => p.id === currentProject.id) ?? null
        : null;
      set({ projects: normalized, currentProject: refreshedCurrent, isLoading: false });
    } catch (error) {
      // On error, set empty projects array
      set({ projects: [], isLoading: false, error: 'Failed to load projects' });
    }
  },
  
  setCurrentProject: (project) => {
    set({ currentProject: project ? normalizeProject(project) : null });
    try {
      const id = (project as any)?.id || null;
      if (id) {
        import('../sentry').then(({ setSentryContextFromRaw }) => {
          setSentryContextFromRaw({ projectId: id });
        }).catch(() => {});
      } else {
        import('../sentry').then(({ clearSentryContext }) => {
          clearSentryContext();
        }).catch(() => {});
      }
    } catch {}
  },
  setSubscriptionAck: (projectId) => set({ subscribedProjectId: projectId, subscriptionAckAt: projectId ? Date.now() : null }),
  isSubscribedReady: (projectId) => {
    const { subscribedProjectId } = get();
    if (!projectId) return !!subscribedProjectId;
    return String(subscribedProjectId || '') === String(projectId);
  },
  
  createProject: async (name: string, description?: string) => {
    try {
      const response = await apiRequest('POST', '/api/projects', {
        name,
        description,
      });
      const project = normalizeProject(await response.json());

      set((state) => ({
        projects: [project, ...state.projects],
      }));

      return project;
    } catch (error) {
      set({ error: 'Failed to create project' });
      throw error;
    }
  },

  getOrCreateDefaultProject: async () => {
    try {
      await get().loadProjects();
      const { projects } = get();
      
      const defaultProject = projects.find((p) => (p as any).isDefault) || projects[0];
      if (defaultProject) {
        set({ currentProject: defaultProject });
        return defaultProject;
      }

      // Non-creating path: respect zero-project state; return a placeholder null
      return Promise.reject(new Error('no_default_project'));
    } catch (error) {
      set({ error: 'Failed to get or create default project' });
      throw error;
    }
  },
  
  updateProject: async (id: string, updates: Partial<Project>) => {
    try {
      const response = await apiRequest('PATCH', `/api/projects/${id}`, updates);
      const project = normalizeProject(await response.json());

      set((state) => ({
        projects: state.projects.map(p => 
          p.id === id ? { ...p, ...project } : p
        ),
        currentProject: state.currentProject?.id === id 
          ? { ...state.currentProject, ...project }
          : state.currentProject,
      }));
    } catch (error) {
      set({ error: 'Failed to update project' });
      throw error;
    }
  },
  
  deleteProject: async (id: string) => {
    try {
      await apiRequest('DELETE', `/api/projects/${id}`, {});
      // Refetch to ensure entitlement gating and removal are consistent across views
      await get().loadProjects();
      const { projects } = get();
      set((state) => ({
        projects,
        currentProject: state.currentProject?.id === id ? null : state.currentProject,
      }));
    } catch (error) {
      set({ error: 'Failed to delete project' });
      throw error;
    }
  },
}));
